import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import FontAwesome from "react-fontawesome";
import "./Categories.css";
import { serverUrl } from "../../Utils/server";

function Categories() {
  const history = useHistory();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState("");
  const [expandedMap, setExpandedMap] = useState({});

  const [searchText, setSearchText] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [treeRootFilter, setTreeRootFilter] = useState("all");
  const [relationFilter, setRelationFilter] = useState("all");
  const [imageFilter, setImageFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [visibleFilter, setVisibleFilter] = useState("all");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setErrorText("");

        const response = await axios({
          method: "get",
          url: `${serverUrl}/catalog-categories/`,
          headers: {
            "Content-Type": "application/json",
            Authorization: `JWT ${localStorage.getItem("token")}`,
          },
        });

        setCategories(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Categories error:", error);
        setCategories([]);
        setErrorText("No se pudieron cargar las categorías.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((item) => {
      if (item && item._id) {
        map[String(item._id)] = item;
      }
    });
    return map;
  }, [categories]);

  const childrenMap = useMemo(() => {
    const map = {};
    categories.forEach((item) => {
      const parentKey = item?.parentId ? String(item.parentId) : "__root__";
      if (!map[parentKey]) map[parentKey] = [];
      map[parentKey].push(item);
    });
    return map;
  }, [categories]);

  const sortSiblings = (list = []) => {
    return [...list].sort((a, b) => {
      const sortA = Number(a?.sortOrder || 0);
      const sortB = Number(b?.sortOrder || 0);

      if (sortA !== sortB) return sortA - sortB;

      return String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
        sensitivity: "base",
        numeric: true,
      });
    });
  };

  const getParentName = (row) => {
    if (!row?.parentId) return "—";
    return categoryMap[String(row.parentId)]?.name || "—";
  };

  const getPathName = (row) => {
    const visited = new Set();
    const parts = [];
    let current = row;

    while (current && current._id && !visited.has(String(current._id))) {
      visited.add(String(current._id));
      parts.unshift(current.name || "");
      current = current.parentId ? categoryMap[String(current.parentId)] : null;
    }

    return parts.filter(Boolean).join(" / ");
  };

  const parentOptions = useMemo(() => {
    return sortSiblings(categories);
  }, [categories]);

  const hasActiveFilters = useMemo(() => {
    return (
      searchText.trim() !== "" ||
      levelFilter !== "all" ||
      treeRootFilter !== "all" ||
      relationFilter !== "all" ||
      imageFilter !== "all" ||
      activeFilter !== "all" ||
      visibleFilter !== "all"
    );
  }, [
    searchText,
    levelFilter,
    treeRootFilter,
    relationFilter,
    imageFilter,
    activeFilter,
    visibleFilter,
  ]);

  const selectedTreeIds = useMemo(() => {
    if (treeRootFilter === "all") return null;

    const included = new Set();
    const start = categoryMap[String(treeRootFilter)];
    if (!start) return new Set();

    included.add(String(start._id));

    const stack = [...(childrenMap[String(start._id)] || [])];
    let guard = 0;

    while (stack.length && guard < 5000) {
      const current = stack.shift();
      const id = String(current?._id || "");
      if (!id || included.has(id)) {
        guard += 1;
        continue;
      }

      included.add(id);
      const children = childrenMap[id] || [];
      children.forEach((child) => stack.push(child));
      guard += 1;
    }

    return included;
  }, [treeRootFilter, categoryMap, childrenMap]);

  const filteredCategories = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    const directlyMatched = categories.filter((row) => {
      const rowId = String(row?._id || "");
      const parentName = getParentName(row).toLowerCase();
      const pathName = getPathName(row).toLowerCase();
      const rowName = String(row?.name || "").toLowerCase();
      const level = String(row?.level || "");
      const hasChildren = (childrenMap[rowId] || []).length > 0;
      const isLeaf = !hasChildren;
      const hasImage = Boolean(String(row?.image || "").trim());

      const searchOk =
        normalizedSearch === "" ||
        rowName.includes(normalizedSearch) ||
        parentName.includes(normalizedSearch) ||
        pathName.includes(normalizedSearch);

      const levelOk = levelFilter === "all" || level === levelFilter;

      const treeRootOk =
        treeRootFilter === "all"
          ? true
          : selectedTreeIds instanceof Set
          ? selectedTreeIds.has(rowId)
          : false;

      const relationOk =
        relationFilter === "all"
          ? true
          : relationFilter === "root"
          ? !row?.parentId
          : relationFilter === "has-parent"
          ? Boolean(row?.parentId)
          : relationFilter === "has-children"
          ? hasChildren
          : relationFilter === "leaf"
          ? isLeaf
          : true;

      const imageOk =
        imageFilter === "all"
          ? true
          : imageFilter === "with-image"
          ? hasImage
          : imageFilter === "without-image"
          ? !hasImage
          : true;

      const activeOk =
        activeFilter === "all"
          ? true
          : activeFilter === "active"
          ? Boolean(row?.isActive)
          : !Boolean(row?.isActive);

      const visibleOk =
        visibleFilter === "all"
          ? true
          : visibleFilter === "visible"
          ? Boolean(row?.isVisible)
          : !Boolean(row?.isVisible);

      return (
        searchOk &&
        levelOk &&
        treeRootOk &&
        relationOk &&
        imageOk &&
        activeOk &&
        visibleOk
      );
    });

    if (!hasActiveFilters) {
      return categories;
    }

    const includedIds = new Set();

    const includeAncestors = (row) => {
      let current = row;
      let guard = 0;

      while (current && current._id && guard < 20) {
        const id = String(current._id);
        if (includedIds.has(id)) break;
        includedIds.add(id);

        current = current.parentId ? categoryMap[String(current.parentId)] : null;
        guard += 1;
      }
    };

    directlyMatched.forEach((row) => {
      includedIds.add(String(row._id));
      includeAncestors(row);
    });

    return categories.filter((row) => includedIds.has(String(row._id)));
  }, [
    categories,
    searchText,
    levelFilter,
    treeRootFilter,
    relationFilter,
    imageFilter,
    activeFilter,
    visibleFilter,
    categoryMap,
    childrenMap,
    hasActiveFilters,
    selectedTreeIds,
  ]);

  const treeData = useMemo(() => {
    const byParent = {};

    filteredCategories.forEach((item) => {
      const parentKey =
        item?.parentId && filteredCategories.some((x) => String(x._id) === String(item.parentId))
          ? String(item.parentId)
          : "__root__";

      if (!byParent[parentKey]) byParent[parentKey] = [];
      byParent[parentKey].push(item);
    });

    Object.keys(byParent).forEach((key) => {
      byParent[key] = sortSiblings(byParent[key]);
    });

    const roots = byParent["__root__"] || [];

    return { byParent, roots };
  }, [filteredCategories]);

  useEffect(() => {
    const nextExpanded = {};
    Object.keys(treeData.byParent).forEach((parentKey) => {
      if (parentKey !== "__root__" && (treeData.byParent[parentKey] || []).length > 0) {
        nextExpanded[parentKey] = true;
      }
    });
    setExpandedMap(nextExpanded);
  }, [
    treeData,
    searchText,
    levelFilter,
    treeRootFilter,
    relationFilter,
    imageFilter,
    activeFilter,
    visibleFilter,
  ]);

  const visibleRows = useMemo(() => {
    const rows = [];

    const walk = (items = []) => {
      items.forEach((item) => {
        const id = String(item._id || "");
        const children = treeData.byParent[id] || [];
        const hasChildren = children.length > 0;

        rows.push({
          ...item,
          __hasChildren: hasChildren,
          __expanded: hasChildren ? Boolean(expandedMap[id]) : false,
        });

        if (hasChildren && expandedMap[id]) {
          walk(children);
        }
      });
    };

    walk(treeData.roots);
    return rows;
  }, [treeData, expandedMap]);

  const toggleExpand = (row) => {
    const id = String(row?._id || "");
    if (!id) return;

    setExpandedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const expandAll = () => {
    const next = {};
    Object.keys(treeData.byParent).forEach((parentKey) => {
      if (parentKey !== "__root__" && (treeData.byParent[parentKey] || []).length > 0) {
        next[parentKey] = true;
      }
    });
    setExpandedMap(next);
  };

  const collapseAll = () => {
    const next = {};
    Object.keys(treeData.byParent).forEach((parentKey) => {
      if (parentKey !== "__root__" && (treeData.byParent[parentKey] || []).length > 0) {
        next[parentKey] = false;
      }
    });
    setExpandedMap(next);
  };

  const clearFilters = () => {
    setSearchText("");
    setLevelFilter("all");
    setTreeRootFilter("all");
    setRelationFilter("all");
    setImageFilter("all");
    setActiveFilter("all");
    setVisibleFilter("all");
  };

  const renderCategoryName = (row) => {
    const level = Number(row?.level || 1);
    const indent = Math.max(level - 1, 0) * 26;

    return (
      <div className="category-tree-cell" style={{ paddingLeft: `${indent}px` }}>
        {row?.__hasChildren ? (
          <button
            type="button"
            className="tree-toggle"
            onClick={() => toggleExpand(row)}
            title={row?.__expanded ? "Collapse" : "Expand"}
          >
            {row?.__expanded ? "▼" : "▶"}
          </button>
        ) : (
          <span className="tree-toggle-spacer"></span>
        )}

        {level > 1 ? (
          <span className="tree-branch">└──</span>
        ) : (
          <span className="tree-root">●</span>
        )}

        <span className="tree-label">{row?.name || ""}</span>
      </div>
    );
  };

  const renderImage = (row) => {
    if (row?.image) {
      return (
        <img
          className="category-thumb"
          src={row.image}
          alt={row?.name || "category"}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
            const next = e.currentTarget.nextSibling;
            if (next) next.style.display = "flex";
          }}
        />
      );
    }

    return null;
  };

  const goNewCategory = () => {
    history.push("/categories/new");
  };

  const goAddChild = (row) => {
    if (Number(row?.level || 1) >= 3) {
      alert("Una categoría de nivel 3 no puede tener hijos.");
      return;
    }

    history.push(`/categories/new?parentId=${row._id}`);
  };

  const goEdit = (row) => {
    history.push(`/categories/edit/${row._id}`);
  };

  const deleteCategory = async (row) => {
    const hasChildren = categories.some(
      (item) => String(item?.parentId || "") === String(row?._id || "")
    );

    if (hasChildren) {
      alert("No se puede eliminar esta categoría porque tiene hijos.");
      return;
    }

    const ok = window.confirm(`¿Eliminar la categoría "${row?.name || ""}"?`);
    if (!ok) return;

    try {
      await axios({
        method: "post",
        url: `${serverUrl}/catalog-categories/remove`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${localStorage.getItem("token")}`,
        },
        data: {
          id: row._id,
        },
      });

      setCategories((prev) => prev.filter((item) => item._id !== row._id));
    } catch (error) {
      console.error("Delete category error:", error);
      const msg =
        error?.response?.data?.msg ||
        error?.response?.data ||
        "No se pudo eliminar la categoría.";
      alert(String(msg));
    }
  };

  return (
    <div className="categories page">
      <div className="head">
        <p>Categories</p>

        <div className="head-actions">
          <button type="button" className="btn-secondary" onClick={expandAll}>
            Expand all
          </button>
          <button type="button" className="btn-secondary" onClick={collapseAll}>
            Collapse all
          </button>
          <button type="button" onClick={goNewCategory}>
            + Add New Category
          </button>
        </div>
      </div>

      <div className="categories-filters">
        <div className="filter-group filter-search">
          <label>Buscar</label>
          <input
            type="text"
            placeholder="Nombre, ruta o padre..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Nivel</label>
          <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}>
            <option value="all">Todos</option>
            <option value="1">Nivel 1</option>
            <option value="2">Nivel 2</option>
            <option value="3">Nivel 3</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Árbol desde</label>
          <select value={treeRootFilter} onChange={(e) => setTreeRootFilter(e.target.value)}>
            <option value="all">Todos</option>
            {parentOptions.map((row) => (
              <option key={row._id} value={row._id}>
                {getPathName(row)}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Relación</label>
          <select value={relationFilter} onChange={(e) => setRelationFilter(e.target.value)}>
            <option value="all">Todas</option>
            <option value="root">Raíz</option>
            <option value="has-parent">Con padre</option>
            <option value="has-children">Con hijos</option>
            <option value="leaf">Hoja</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Imagen</label>
          <select value={imageFilter} onChange={(e) => setImageFilter(e.target.value)}>
            <option value="all">Todas</option>
            <option value="with-image">Con imagen</option>
            <option value="without-image">Sin imagen</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Activa</label>
          <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
            <option value="all">Todas</option>
            <option value="active">Sí</option>
            <option value="inactive">No</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Visible</label>
          <select value={visibleFilter} onChange={(e) => setVisibleFilter(e.target.value)}>
            <option value="all">Todas</option>
            <option value="visible">Sí</option>
            <option value="hidden">No</option>
          </select>
        </div>

        <div className="filter-group filter-actions">
          <label>&nbsp;</label>
          <button type="button" className="btn-clear-filters" onClick={clearFilters}>
            Limpiar filtros
          </button>
        </div>
      </div>

      <div className="categories-summary">
        <span>Total base: {categories.length}</span>
        <span>Total filtrado: {filteredCategories.length}</span>
        <span>Total visible en árbol: {visibleRows.length}</span>
      </div>

      {loading ? (
        <div className="categories-message">Loading...</div>
      ) : errorText ? (
        <div className="categories-message error">{errorText}</div>
      ) : visibleRows.length === 0 ? (
        <div className="categories-message">No Categories</div>
      ) : (
        <div className="categories-table-wrap">
          <table className="categories-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Imagen</th>
                <th>Categoría</th>
                <th>Nivel</th>
                <th>Padre</th>
                <th>Ruta</th>
                <th>Activa</th>
                <th>Visible</th>
                <th>Creada</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <tr key={row?._id || index}>
                  <td>{index + 1}</td>

                  <td>
                    <div className="category-image-wrap">
                      {renderImage(row)}
                      <div
                        className="category-thumb-placeholder"
                        style={{ display: row?.image ? "none" : "flex" }}
                      >
                        <FontAwesome name="image" />
                      </div>
                    </div>
                  </td>

                  <td>{renderCategoryName(row)}</td>
                  <td>{row?.level ?? ""}</td>
                  <td>{getParentName(row)}</td>
                  <td>{getPathName(row)}</td>
                  <td>{row?.isActive ? "Sí" : "No"}</td>
                  <td>{row?.isVisible ? "Sí" : "No"}</td>
                  <td>{row?.createdOn ? String(row.createdOn).split("T")[0] : ""}</td>
                  <td>
                    <div className="category-actions">
                      {Number(row?.level || 1) < 3 && (
                        <button className="btn-action btn-child" onClick={() => goAddChild(row)}>
                          <FontAwesome name="plus" /> Hijo
                        </button>
                      )}
                      <button className="btn-action btn-edit" onClick={() => goEdit(row)}>
                        <FontAwesome name="pencil" /> Editar
                      </button>
                      <button className="btn-action btn-delete" onClick={() => deleteCategory(row)}>
                        <FontAwesome name="trash" /> Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Categories;
